import { Component } from '@theme/component';
import { onMotionChange, setSitePaused, sitePaused } from '@theme/bdc-motion';

/**
 * Braun Design Co "Pause animations" button (sections/bdc-footer.liquid, in the bottom row
 * of the footer on every page).
 *
 * One press pauses every motion piece on the site and is remembered on later visits;
 * `@theme/bdc-motion` saves the choice, sets `data-bdc-motion` on <html> and tells every
 * piece. The two loops (the hero's storefronts and the logo grid) render paused in the same
 * layout, and their own Play buttons can still start them for that page view.
 *
 * - The label never changes; `aria-pressed` carries the state (the APG toggle button
 *   pattern), and the pressed look is a fill plus a check in place of the pause icon, so
 *   it never relies on color alone.
 * - The section renders this element `hidden`, so without JavaScript there is no button
 *   that cannot work. This module shows it unless the device asks for reduced motion,
 *   which already stops everything: the button can never turn motion on. If reduced
 *   motion turns on while the button has focus, it hides once focus leaves, so focus is
 *   never dropped to the page.
 * - It follows a change made in another tab, through `onMotionChange`.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button - The toggle button.
 *
 * @extends {Component<Refs>}
 */
class BdcMotionToggle extends Component {
  requiredRefs = ['button'];

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** @type {AbortController | undefined} */
  #abortController;

  /** Waiting for focus to leave before hiding. */
  #hideQueued = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    onMotionChange(this.#render, { signal: this.#abortController.signal });

    this.#render();
  }

  updatedCallback() {
    super.updatedCallback();

    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#hideQueued = false;
  }

  /**
   * Pauses or resumes every motion piece on the site. Bound to the button with `on:click`.
   */
  toggle() {
    setSitePaused(!sitePaused());
  }

  /** Shows the button unless the device asks for reduced motion, with the current state. */
  #render = () => {
    this.refs.button.setAttribute('aria-pressed', String(sitePaused()));

    const hide = this.#reducedMotion.matches;
    if (!hide || !this.contains(document.activeElement)) {
      this.hidden = hide;
      return;
    }

    if (this.#hideQueued) return;
    this.#hideQueued = true;
    this.addEventListener(
      'focusout',
      () => {
        this.#hideQueued = false;
        this.hidden = this.#reducedMotion.matches;
      },
      { once: true, signal: this.#abortController?.signal }
    );
  };
}

if (!customElements.get('bdc-motion-toggle')) {
  customElements.define('bdc-motion-toggle', BdcMotionToggle);
}
